import { CloseIcon } from '@/assets/icons/close-icon'
import { FilterIcon } from '@/assets/icons/filter-icon'
import { FilterButton } from '@/components/buttons/filter-button'
import { DatePicker } from '@/components/inputs/date-picker'
import { SearchSelect } from '@/components/inputs/search-select/search-select'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DEADLINE } from '@/constants/deadline'
import { PopoverClose } from '@radix-ui/react-popover'
import { useTableFilter } from './hooks/use-table-filter'
import { Controller } from 'react-hook-form'
import { REQUEST_STATUS } from '@/constants/request-status'
import type { TableHead } from '@/types/table/table-head'
import { Show } from '@/components/show'
import { getUniquePreviousEducations } from '@/services/education/get-unique-previous-educations'
import { QUERIES } from '@/constants/queries'
import { getUniqueDesiredEducations } from '@/services/education/get-unique-desired-educations'
import { getUniquePreviousEducationalInstitutions } from '@/services/education/get-unique-previous-educational-institutions'

interface TableFilterProps {
  heads: TableHead[]
}

export const TableFilter = ({ heads }: TableFilterProps) => {
  const {
    states: { control, errors },
    actions: { reset, watch, handleSubmit, clearErrors },
  } = useTableFilter()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterButton>Filtrar</FilterButton>
      </PopoverTrigger>
      <PopoverContent className="w-100 border border-[var(--brand-blue-3)] rounded-lg me-12 mt-[-3rem]">
        <div className="w-full bg-[var(--brand-blue-1)]/20 p-4 pb-6 px-8">
          <div className="w-full flex justify-between pb-4">
            <Button
              onClick={reset}
              variant={'ghost'}
              className="underline text-base px-0 h-8 w-min hover:bg-transparent text-[var(--brand-blue-3)] hover:text-[var(--brand-blue-3)]"
            >
              Limpar
            </Button>

            <p className="flex gap-2 items-center font-semibold text-base text-[var(--brand-blue-3)] hover:text-[var(--brand-blue-3)]">
              Filtrar
              <FilterIcon />
            </p>

            <PopoverClose className="h-8 px-0 cursor-pointer text-[var(--brand-blue-3)]">
              <CloseIcon />
            </PopoverClose>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <p className="mb-2 font-bold text-sm">Por Data de Abertura</p>
              <div className="grid grid-cols-2 gap-2">
                <Show when={heads[1]?.hidden === false}>
                  <div className="w-full">
                    <Controller
                      name="openingDateStart"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          date={watch('openingDateStart')}
                          setDate={(date) => {
                            field.onChange(date)
                            clearErrors(['openingDateStart', 'openingDateEnd'])
                          }}
                          error={errors.openingDateStart}
                        />
                      )}
                    />
                  </div>
                </Show>
                <Show when={heads[1]?.hidden === false}>
                  <div className="w-full">
                    <Controller
                      name="openingDateEnd"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          date={watch('openingDateEnd')}
                          setDate={(date) => {
                            field.onChange(date)
                            clearErrors(['openingDateStart', 'openingDateEnd'])
                          }}
                          error={errors.openingDateEnd}
                        />
                      )}
                    />
                  </div>
                </Show>
              </div>
            </div>

            <Show when={heads[2]?.hidden === false}>
              <div>
                <p className="mb-2 font-bold text-sm">Por Prazo</p>
                <Controller
                  name="deadline"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={watch('deadline')}
                    >
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DEADLINE.UP_TO_DATE.id}>
                          {DEADLINE.UP_TO_DATE.name}
                        </SelectItem>
                        <SelectItem value={DEADLINE.DUE.id} defaultChecked>
                          {DEADLINE.DUE.name}
                        </SelectItem>
                        <SelectItem value={DEADLINE.OVERDUE.id}>
                          {DEADLINE.OVERDUE.name}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </Show>

            <Show when={heads[4]?.hidden === false}>
              <div>
                <p className="mb-2 font-bold text-sm">Por Curso Antigo</p>
                <Controller
                  name="oldCourse"
                  control={control}
                  render={({ field }) => (
                    <SearchSelect
                      queryFn={getUniquePreviousEducations}
                      queryKey={[QUERIES.QUERY_GET_PREVIOUS_EDUCATIONS]}
                      onChange={field.onChange}
                      value={watch('oldCourse')}
                    />
                  )}
                />
              </div>
            </Show>

            <Show when={heads[4]?.hidden === false}>
              <div>
                <p className="mb-2 font-bold text-sm">Por Curso Desejado</p>
                <Controller
                  name="desiredCourse"
                  control={control}
                  render={({ field }) => (
                    <SearchSelect
                      queryFn={getUniqueDesiredEducations}
                      queryKey={[QUERIES.QUERY_GET_DESIRED_EDUCATIONS]}
                      onChange={field.onChange}
                      value={watch('desiredCourse')}
                    />
                  )}
                />
              </div>
            </Show>
            <Show when={heads[4]?.hidden === false}>
              <div>
                <p className="mb-2 font-bold text-sm">Por IES Anterior</p>
                <Controller
                  name="previousIES"
                  control={control}
                  render={({ field }) => (
                    <SearchSelect
                      queryFn={getUniquePreviousEducationalInstitutions}
                      queryKey={[
                        QUERIES.QUERY_GET_PREVIOUS_EDUCATIONAL_INSTITUTIONS,
                      ]}
                      onChange={field.onChange}
                      value={watch('previousIES')}
                    />
                  )}
                />
              </div>
            </Show>
            <Show when={heads[7]?.hidden === false}>
              <div>
                <p className="mb-2 font-bold text-sm">Por Status</p>
                <Controller
                  name="status"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={watch('status')}
                    >
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={REQUEST_STATUS.UNDER_REVIEW.id}>
                          {REQUEST_STATUS.UNDER_REVIEW.name}
                        </SelectItem>
                        <SelectItem
                          value={REQUEST_STATUS.GRANT.id}
                          defaultChecked
                        >
                          {REQUEST_STATUS.GRANT.name}
                        </SelectItem>
                        <SelectItem value={REQUEST_STATUS.DENY.id}>
                          {REQUEST_STATUS.DENY.name}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </Show>
            <div className="flex justify-between">
              <div className="w-36">
                <PopoverClose asChild>
                  <Button
                    type="button"
                    size={'lg'}
                    variant={'light'}
                    className="bg-transparent shadow-md font-bold"
                  >
                    Cancelar
                  </Button>
                </PopoverClose>
              </div>
              <div className="w-36">
                <Button size={'lg'} className="shadow-md">
                  Aplicar
                </Button>
              </div>
            </div>
          </form>
        </div>
      </PopoverContent>
    </Popover>
  )
}
